import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Menu } from 'primereact/menu';
import PropTypes from 'prop-types';
import { useMemo, useRef, useState } from 'react';
import { useForm, useFormState } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'layout';
import { TOAST } from 'constant';
import Loading from 'components/Loading';
import ErrorDialog from 'components/ErrorDialog';
import {
  createReviews,
  downloadDocxFile,
  downloadDocxTemplate,
  downloadPdfFile,
  downloadPdfTemplate,
  getReviewerInfo,
  getReviewsDetail,
  submitReviews,
  updateReviews,
} from 'apis/reviews.api';
import {
  catchError,
  getAcadamicLevelShortHand,
  isMemberAllowedReview,
  toNonAccentVietnamese,
} from 'utils/func';
import { handleTypeData, initialValues, setUndefinedToNull } from 'utils/func/review';
import { validateGeneralInfoWithPayment } from 'utils/validation/generalInfo';
import './style.scss';

export default function DynamicPrequalificationForm({
  projectProposalId,
  councilMemberId,
  councilId,
  generalInfo,
  isLoading,
  readOnly,
}) {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const [isVisibleSubmitDialog, setIsVisibleSubmitDialog] = useState(false);
  const [isVisibleErrorDialog, setIsVisibleDialogError] = useState(false);
  const [isSubmit, setIsSubmit] = useState(false);
  const downloadTemplateButtonRef = useRef();
  const downloadButtonRef = useRef();

  const {
    mutateAsync: mutateAsyncDownloadDocxTemplate,
    isLoading: isDownloadDocxTemplateLoading,
    isFetching: isDownloadDocxTemplateFetching,
  } = useMutation(downloadDocxTemplate);
  const {
    mutateAsync: mutateAsyncDownloadDocxFile,
    isLoading: isDownloadDocxFileLoading,
    isFetching: isDownloadDocxFileFetching,
  } = useMutation(downloadDocxFile);
  const {
    mutateAsync: mutateAsyncDownloadPdfFile,
    isLoading: isDownloadPdfFileLoading,
    isFetching: isDownloadPdfFileFetching,
  } = useMutation(downloadPdfFile);
  const {
    mutateAsync: mutateAsyncDownloadPdfTemplate,
    isLoading: isDownloadPdfTemplateLoading,
    isFetching: isDownloadPdfTemplateFetching,
  } = useMutation(downloadPdfTemplate);

  const {
    data: _data,
    isLoading: isGetReviewsDetailLoading,
    refetch,
  } = useQuery(['reviews', councilMemberId, projectProposalId, councilId], () =>
    getReviewsDetail(councilMemberId, projectProposalId, councilId)
  );

  const reviewsData = useMemo(() => _data?.data ?? {}, [_data]);

  const dataTemplate = useMemo(
    () =>
      JSON.parse(
        t('lang') === 'vi'
          ? reviewsData?.review?.content ?? null
          : reviewsData?.review?.contentEn ?? null
      ) ?? {},
    [reviewsData, t]
  );

  const {
    data: _dataReviewerInfo,
    isLoading: isGetReviewerInfoLoading,
    refetch: refetchReviewerInfo,
  } = useQuery(['reviewerInfo', projectProposalId, councilMemberId, councilId], () =>
    getReviewerInfo(projectProposalId, councilMemberId, councilId)
  );

  const reviewerInfo = useMemo(() => {
    const { councilMembers, ...rest } = _dataReviewerInfo?.data ?? {};
    return rest ?? {};
  }, [_dataReviewerInfo]);

  const { mutateAsync: mutateAsyncCreateReviews, isLoading: isCreateReviewsLoading } =
    useMutation(createReviews);
  const { mutateAsync: mutateAsyncUpdateReviews, isLoading: isUpdateReviewsLoading } =
    useMutation(updateReviews);
  const {
    mutateAsync: mutateAsyncSubmitReviews,
    isError: isErrorSubmit,
    error: errorSubmit,
  } = useMutation(submitReviews);

  function getAcadamicLevelShortHand1(opts) {
    const { academicRank, academicDegree, fullname } = opts;

    return `${getAcadamicLevelShortHand(academicRank, academicDegree, t, true)} ${fullname}`;
  }

  const {
    control,
    formState: { errors },
    setValue,
    handleSubmit,
    watch,
    reset,
  } = useForm({});

  const { isDirty, dirtyFields } = useFormState({
    control,
  });

  const existedData = {
    generalInfo: {
      ...generalInfo,
      ownerName: getAcadamicLevelShortHand1({
        academicRank: generalInfo?.projectProposalContacts?.[0]?.academicRank,
        academicDegree: generalInfo?.projectProposalContacts?.[0]?.academicDegree,
        fullname: generalInfo?.projectProposalContacts?.[0]?.fullname,
      }),
      organization: {
        name:
          t('lang') === 'en'
            ? generalInfo?.projectProposalOrganizations?.[0]?.englishName
            : generalInfo?.projectProposalOrganizations?.[0]?.name,
      },
    },
    reviewerInfo: {
      ...reviewerInfo,
      fullname: getAcadamicLevelShortHand1({
        academicRank: reviewerInfo?.scientificProfile?.academicRank,
        academicDegree: reviewerInfo?.scientificProfile?.academicDegree,
        fullname: reviewerInfo?.fullname,
      }),
      organization: {
        name:
          t('lang') === 'en'
            ? reviewerInfo?.organization?.englishName
            : reviewerInfo?.organization?.name,
      },
    },
  };

  initialValues({
    control,
    errors,
    reset,
    watch,
    setValue,
    refetchReviewerInfo,
    reviewerInfo,
    existedData,
    readOnly,
  });

  const onSubmit = async (data) => {
    // Kiểm tra general info trước
    const generalInfoError = validateGeneralInfoWithPayment(generalInfo, reviewerInfo, t);
    if (generalInfoError) {
      toast(TOAST.ERROR, generalInfoError);
      return;
    }

    // Kiểm tra chữ ký trước khi save
    if (!reviewerInfo?.signatureUrl) {
      toast(TOAST.ERROR, t('council.review.signatureRequired'));
      return;
    }

    const reviewId = _data?.data?.review?.id;
    const body = {
      projectProposalId,
      councilId,
      councilMemberId,
      reviewTemplateId: _data?.data?.review?.reviewTemplateId,
      reviewValues: {
        ...(setUndefinedToNull(data) ?? {}),
      },
    };
    if (reviewId) {
      body.id = reviewId;
      try {
        await mutateAsyncUpdateReviews(body);
        toast(TOAST.SUCCESS, t('council.review.updateSuccess'));
        reset({}, { keepValues: true });
        setIsSubmit(false);
        refetch();
      } catch (error) {
        catchError(t, error);
      }
    } else {
      try {
        await mutateAsyncCreateReviews(body);
        toast(TOAST.SUCCESS, t('council.review.createSuccess'));
        reset({}, { keepValues: true });
        setIsSubmit(false);
        refetch();
      } catch (error) {
        catchError(t, error);
      }
    }
  };

  const handleDownloadDocxTemplate = async () => {
    try {
      const res = await mutateAsyncDownloadDocxTemplate({
        projectProposalId,
        councilId,
        councilMemberId,
      });
      if (res?.data) {
        const file = new Blob([res.data]);

        const filename = 'Phieu nhan xet danh gia so tuyen.docx';

        const link = document.createElement('a');
        link.href = URL.createObjectURL(file);
        link.download = filename;
        link.click();
      }
    } catch (error) {
      toast(TOAST.ERROR, t('projectProposal.review.exportDocxErrorTitle'));
    }
  };
  const handleDownloadDocxFile = async () => {
    try {
      const res = await mutateAsyncDownloadDocxFile({
        projectProposalId,
        councilId,
        councilMemberId,
      });
      if (res?.data) {
        const file = new Blob([res.data]);
        const academicLevelShorthand = getAcadamicLevelShortHand(
          reviewerInfo?.scientificProfile?.academicRank,
          reviewerInfo?.scientificProfile?.academicDegree,
          t,
          true
        );
        const filename = `Phieu nhan xet danh gia so tuyen - ${academicLevelShorthand}${toNonAccentVietnamese(
          reviewerInfo?.fullname
        )}.docx`;

        const link = document.createElement('a');
        link.href = URL.createObjectURL(file);
        link.download = filename;
        link.click();
      }
    } catch (error) {
      if (error?.response?.status === 490) {
        toast(TOAST.ERROR, t('errorMessage.scientistNotReviewOnlineFormYet'));
      } else {
        toast(TOAST.ERROR, t('projectProposal.review.exportDocxErrorTitle'));
      }
    }
  };

  const handleDownloadPdfFile = async () => {
    try {
      const res = await mutateAsyncDownloadPdfFile({
        projectProposalId,
        councilId,
        councilMemberId,
      });
      if (res?.data) {
        const file = new Blob([res.data]);
        const academicLevelShorthand = getAcadamicLevelShortHand(
          reviewerInfo?.scientificProfile?.academicRank,
          reviewerInfo?.scientificProfile?.academicDegree,
          t,
          true
        );
        const filename = `Phieu nhan xet danh gia so tuyen - ${academicLevelShorthand}${toNonAccentVietnamese(
          reviewerInfo?.fullname
        )}.pdf`;

        const link = document.createElement('a');
        link.href = URL.createObjectURL(file);
        link.download = filename;
        link.click();
      }
    } catch (error) {
      if (error?.response?.status === 490) {
        toast(TOAST.ERROR, t('errorMessage.scientistNotReviewOnlineFormYet'));
      } else {
        toast(TOAST.ERROR, t('projectProposal.review.exportPdfErrorTitle'));
      }
    }
  };

  const handleDownloadPdfTemplate = async () => {
    try {
      const res = await mutateAsyncDownloadPdfTemplate({
        projectProposalId,
        councilId,
        councilMemberId,
      });
      if (res?.data) {
        const file = new Blob([res.data]);

        const filename = 'Phieu nhan xet danh gia so tuyen.pdf';

        const link = document.createElement('a');
        link.href = URL.createObjectURL(file);
        link.download = filename;
        link.click();
      }
    } catch (error) {
      toast(TOAST.ERROR, t('projectProposal.review.exportPdfErrorTitle'));
    }
  };

  const handleSubmitReview = () => {
    // Kiểm tra chữ ký trước khi submit
    if (!reviewerInfo?.signatureUrl) {
      toast(TOAST.ERROR, t('council.review.signatureRequired'));
      return;
    }
    setIsVisibleSubmitDialog(true);
  };

  const onConfirmSubmit = async () => {
    const submitData = {
      projectProposalId,
      councilId,
      councilMemberId,
    };
    try {
      await mutateAsyncSubmitReviews(submitData);
      reset({}, { keepValues: true });
      toast(TOAST.SUCCESS, t('council.review.submitSuccess'));
      setIsSubmit(true);
      setIsVisibleSubmitDialog(false);
    } catch (error) {
      catchError(t, error);
      setIsVisibleDialogError(true);
    }
  };

  const footerConfirmSubmit = () => (
    <div>
      <Button
        label={t('formLayout.action.cancel')}
        className="w-7rem"
        severity="danger"
        onClick={() => setIsVisibleSubmitDialog(false)}
        autoFocus
      />
      <Button
        label={t('formLayout.action.ok')}
        className="w-7rem"
        severity="info"
        onClick={onConfirmSubmit}
      />
    </div>
  );
  return (
    <div className="p-5 bg-white dynamic-form">
      {(isLoading ||
        isGetReviewsDetailLoading ||
        isGetReviewerInfoLoading ||
        isCreateReviewsLoading ||
        isUpdateReviewsLoading) && <Loading />}
      <div className="flex justify-content-end mb-4">
        {reviewsData?.review?.id && (
          <Button
            label={t('council.review.exportFile')}
            icon={`pi ${
              isDownloadDocxFileLoading ||
              isDownloadDocxFileFetching ||
              isDownloadPdfFileLoading ||
              isDownloadPdfFileFetching
                ? 'pi-spin pi-spinner'
                : 'pi-file'
            }`}
            className="block w-auto h-3rem border-none"
            severity="info"
            onClick={(event) => downloadButtonRef.current.toggle(event)}
            disabled={
              isDownloadDocxFileLoading ||
              isDownloadDocxFileFetching ||
              isDownloadPdfFileLoading ||
              isDownloadPdfFileFetching
            }
          />
        )}
        <Button
          label={t('council.review.downloadTemplate')}
          icon={`pi ${
            isDownloadDocxTemplateLoading ||
            isDownloadDocxTemplateFetching ||
            isDownloadPdfTemplateLoading ||
            isDownloadPdfTemplateFetching
              ? 'pi-spin pi-spinner'
              : 'pi-file'
          }`}
          className="block w-auto h-3rem border-none ml-1"
          severity="info"
          onClick={(event) => downloadTemplateButtonRef.current.toggle(event)}
          disabled={
            isDownloadDocxTemplateLoading ||
            isDownloadDocxTemplateFetching ||
            isDownloadPdfTemplateLoading ||
            isDownloadPdfTemplateFetching
          }
        />
      </div>
      <Menu
        ref={downloadButtonRef}
        model={[
          {
            label: t('council.review.exportPdf'),
            icon: 'pi pi-file-pdf',
            command: handleDownloadPdfFile,
            disabled: isDownloadPdfFileLoading || isDownloadPdfFileFetching,
          },
          {
            label: t('council.review.exportDocx'),
            icon: 'pi pi-file-word',
            command: handleDownloadDocxFile,
            disabled: isDownloadDocxFileLoading || isDownloadDocxFileFetching,
          },
        ]}
        popup
      />
      <Menu
        ref={downloadTemplateButtonRef}
        model={[
          {
            label: t('council.review.downloadPdfTemplate'),
            icon: 'pi pi-file-pdf',
            command: handleDownloadPdfTemplate,
            disabled: isDownloadPdfTemplateLoading || isDownloadPdfTemplateFetching,
          },
          {
            label: t('council.review.downloadDocxTemplate'),
            icon: 'pi pi-file-word',
            command: handleDownloadDocxTemplate,
            disabled: isDownloadDocxTemplateLoading || isDownloadDocxTemplateFetching,
          },
        ]}
        popup
      />
      <form autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
        {Object.keys(dataTemplate).map((key) =>
          handleTypeData({
            ...dataTemplate[key],
            readOnly,
          })
        )}

        <div className="flex justify-content-end mt-8">
          <Button
            label={t('formLayout.action.close')}
            severity="danger"
            type="button"
            onClick={() => navigate(-1)}
            className="mr-2 w-8rem"
          />
          {!readOnly && (
            <>
              <Button
                label={t('formLayout.action.save')}
                severity="info"
                type="submit"
                className="mr-2 w-8rem"
                disabled={!(isDirty && Object.keys(dirtyFields).length)}
              />

              {reviewsData?.review?.id && (
                <Button
                  label={t('formLayout.submit')}
                  severity="success"
                  type="button"
                  onClick={handleSubmitReview}
                  className="w-8rem"
                  disabled={
                    !!reviewsData?.isSubmitted ||
                    (isDirty && Object.keys(dirtyFields).length) ||
                    reviewsData?.reviewDeadline < new Date().toISOString() ||
                    !isMemberAllowedReview(_dataReviewerInfo?.data?.councilMembers?.[0]) ||
                    isSubmit
                  }
                />
              )}
            </>
          )}
        </div>
      </form>
      {isErrorSubmit && (
        <ErrorDialog
          title={t('council.review.update')}
          isError={isErrorSubmit}
          error={errorSubmit}
          visible={isVisibleErrorDialog}
          setVisible={setIsVisibleDialogError}
        />
      )}
      <Dialog
        visible={isVisibleSubmitDialog}
        header={<p className="text-blue-500">{t('council.submit.confirmationTitle')}</p>}
        position="center"
        style={{ width: '410px' }}
        onHide={() => setIsVisibleSubmitDialog(false)}
        footer={() => footerConfirmSubmit()}
      >
        <p className="text-lg py-3">{t('council.submit.confirmationSubmitMessage')}</p>
      </Dialog>
    </div>
  );
}

DynamicPrequalificationForm.propTypes = {
  projectProposalId: PropTypes.string.isRequired,
  councilMemberId: PropTypes.string.isRequired,
  councilId: PropTypes.string.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  generalInfo: PropTypes.object,
  isLoading: PropTypes.bool,
  readOnly: PropTypes.bool,
};

DynamicPrequalificationForm.defaultProps = {
  generalInfo: {},
  isLoading: false,
  readOnly: false,
};
